import { useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import { isEqual, set } from 'lodash';
import dayjs from 'dayjs';
import Yup from '../../utils/yup';
import utc from 'dayjs/plugin/utc';

import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import useMenus from '../../context/MenuContext';
import masterDataService from '../../service/masterData.service';
import useUsers from '../../context/UsersContext';
import { cleanValues, generateAutocompleteItems, getJokerFieldType } from '../../utils/helper';
import { DeleteDialogUI, FormButtonPanelUI, FormHeaderUI, FormBase, ButtonUI } from '../Interface';
import History from '../History/History';
import itemService from '../../service/item.service';
import { ItemFields } from './ItemFields';
import ItemReports from './ItemReports';
import ItemTabs from './ItemTabs';
import useConfig from '../../context/ConfigContext';
import { InvManagementMethod, ItemGroupType } from '../../config/itemGroup';
import { ItemKind, ItemOwnershipType } from '../../config/item';
import { useParams } from 'react-router-dom';
import jokerService from '../../service/joker.service';
import { JokerFieldTypes, JokerTypes } from '../../config/joker';
import { CategoryOptionType } from '../../config/vatCodes';
import { PrintableForms } from '../../config/formsWithPrint';
import ItemLabelDialog from './ItemLabelDialog';
import ItemBatchLabelDialog from './ItemBatchLabelDialog';
import ItemAttachmentPrintDialog from './ItemAttachmentPrintDialog';
import formPrintTemplateService from '../../service/formPrintTemplate.service';
import ItemStockDialog from './ItemStockDialog';
import ContextMenu from './ContextMenu';
import ItemHistoryDialog from './ItemHistoryDialog';
import { isCustomItemCodeGenerationEnabled } from './itemCodeGeneration';
import itemCategoryService from '../../service/itemCategory.service';
import useParam from '../../context/ParamContext';
import { TagTarget } from '../../config/tag';
import itemTypeService from '../../service/itemType.service';
import FilePresentOutlinedIcon from '@mui/icons-material/FilePresentOutlined';
import { IconButton, Tooltip } from '@mui/material';
import SupportingDocumentTypeItemLinkDialog from '../SupportingDocumentType/SupportingDocumentTypeItemLinkDialog';

const normalizeTemplateKey = (key) => {
  if (key === undefined || key === null) {
    return null;
  }

  return String(key);
};

const getPrintableFormConfig = (url, key) => {
  return PrintableForms.find(
    (printableForm) =>
      printableForm.url === url && normalizeTemplateKey(printableForm.key) === normalizeTemplateKey(key)
  );
};

const ITEM_BATCH_LABEL_TEMPLATE_KEY = 3;

const getSelectedItemPropertyAssignments = (selectedValue) => {
  if (Array.isArray(selectedValue?.itemPropertyAssignment)) {
    return selectedValue.itemPropertyAssignment;
  }

  if (Array.isArray(selectedValue?.itemPropertyAssignments)) {
    return selectedValue.itemPropertyAssignments;
  }

  return [];
};

const getSelectedItemPropertyIds = (selectedValue) => {
  return getSelectedItemPropertyAssignments(selectedValue)
    .map((assignment) => assignment.itemTypePropertyId ?? assignment.itemTypeProperty?.id)
    .filter((id) => id !== null && id !== undefined);
};

const getItemTypePropertyIds = (itemTypeDetail) => {
  if (!Array.isArray(itemTypeDetail?.itemTypePropertyLink)) {
    return [];
  }

  return itemTypeDetail.itemTypePropertyLink
    .map((link) => link.itemTypePropertyId ?? link.itemTypeProperty?.id)
    .filter((id) => id !== null && id !== undefined);
};

const hasItemPropertyAssignmentValue = (assignment) => {
  const dataType = assignment?.property?.dataType;

  if (dataType === 'COMBO') {
    return Boolean(assignment?.itemTypePropertyValueId);
  }

  if (dataType === 'NUMBER' || dataType === 'DECIMAL') {
    return assignment?.valueNumber !== '' && assignment?.valueNumber !== null && assignment?.valueNumber !== undefined;
  }

  return Boolean(assignment?.valueText?.trim());
};

export const ItemPage = ({ fromDialog, dialogSetSelectedItem, selectedItemCode, hideRightPanel = false }) => {
  const { itemCode } = useParams();
  const resolvedItemCode = selectedItemCode ?? itemCode;
  const { config, getConfig } = useConfig();
  const { params, getParam } = useParam();
  const { menus, getMenus, getMenuSubMenuId, getCanEdit } = useMenus();
  const { getUser, user } = useUsers();
  const [refetchTable, setRefetchTable] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [viewField, setViewField] = useState(true);
  const [editButtonVisible, setEditButtonvisible] = useState(true);
  const [addField, setAddField] = useState(true);
  const [canEdit, setCanEdit] = useState(true);
  const [submenu, setSubmenu] = useState(null);
  const [itemLabelTemplate, setItemLabelTemplate] = useState(null);
  const [itemBatchLabelTemplate, setItemBatchLabelTemplate] = useState(null);
  const [itemDocumentTemplates, setItemDocumentTemplates] = useState([]);

  const [defaultValues, setDefaultValues] = useState({});
  const [selectedValues, setSelectedValues] = useState({});
  const [operatorsDisabled, setOperatorsDisabled] = useState(false);
  const [initValues, setInitValues] = useState({});
  const [defaultSelectedValues, setDefaultSelectedValues] = useState({});
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [autoCompleteData, setAutoCompleteData] = useState({});
  const [autocompleteOptions, setAutoCompleteOptions] = useState({
    quantityUnit: [],
    itemGroup: [],
    kshClassification: [],
    purchaseVatCode: [],
    salesVatCode: [],
    supplier: [],
  });
  const [jokerFields, setJokerFields] = useState([]);
  const [jokerRefreshKey, setJokerRefreshKey] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [labelDialogState, setLabelDialogState] = useState({ open: false });
  const [attachmentPrintDialogOpen, setAttachmentPrintDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [supportingDocumentTypeDialogOpen, setSupportingDocumentTypeDialogOpen] = useState(false);
  const [itemStockDialogState, setItemStockDialogState] = useState({
    open: false,
    data: null,
  });
  const [itemCategoryItemLink, setItemCategoryItemLink] = useState([]);
  const [tagItemLink, setTagItemLink] = useState([]);

  const defContextMenu = { position: null };
  const [contextMenu, setContextMenu] = useState(defContextMenu);

  const handleItemStockDialogClose = () => {
    setItemStockDialogState({ open: false, data: null });
  };

  const getJokerFields = () => {
    jokerService
      .getFields(JokerTypes.ITEM)
      .then((data) => {
        setJokerFields(data);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (fromDialog && Object.keys(selectedValues).length > 0) {
      dialogSetSelectedItem(selectedValues.itemCode ?? null);
    }
  }, [selectedValues]);

  const defRelatedItem = {
    isNew: true,
    relationType: null,
    relatedItemCode: null,
  };

  const def = {
    itemCode: '',
    itemName: '',
    itemGroupId: null,
    type: '',
    kind: ItemKind.NORMAL,
    ownershipType: null,
    stockItemType: '',
    inPurchase: false,
    inSales: false,
    weight: '',
    kshClassificationId: null,
    other: '',
    active: true,
    invManagementMethod: InvManagementMethod.NONE,
    quantityUnitId: null,
    itemPrice: [],
    class1: false,
    class2: false,
    class3: false,
    class4: false,
    class5: false,
    class6: false,
    class7: false,
    class8: false,
    class9: false,
    class10: false,
    class11: false,
    class12: false,
    class13: false,
    class14: false,
    class15: false,
    class16: false,
    class17: false,
    class18: false,
    class19: false,
    class20: false,
    joker_char1: '',
    joker_char2: '',
    joker_char3: '',
    joker_char4: '',
    joker_char5: '',
    joker_char6: '',
    joker_char7: '',
    joker_char8: '',
    joker_char9: '',
    joker_char10: '',
    joker_char11: '',
    joker_char12: '',
    joker_char13: '',
    joker_char14: '',
    joker_char15: '',
    joker_char16: '',
    joker_char17: '',
    joker_char18: '',
    joker_char19: '',
    joker_char20: '',
    joker_char21: '',
    joker_char22: '',
    joker_char23: '',
    joker_char24: '',
    joker_char25: '',
    joker_char26: '',
    joker_char27: '',
    joker_char28: '',
    joker_char29: '',
    joker_char30: '',
    joker_char31: '',
    joker_char32: '',
    joker_char33: '',
    joker_char34: '',
    joker_char35: '',
    joker_char36: '',
    joker_date1: null,
    joker_date2: null,
    joker_date3: null,
    joker_date4: null,
    joker_date5: null,
    joker_num1: '',
    joker_num2: '',
    joker_num3: '',
    joker_num4: '',
    joker_num5: '',
    joker_int1: '',
    joker_int2: '',
    joker_int3: '',
    joker_int4: '',
    joker_int5: '',
    barCode: '',
    landedCost: false,
    purchaseVatCode: null,
    salesVatCode: null,
    drawingNumber: '',
    itemAttachment: [],
    defSupplier: null,
    autoGenerateItemCode: null,
    itemCategoryItemLink: [],
    tagItemLink: [],
    description: '',
    itemTypeId: null,
    itemPropertyAssignment: [],
    relatedItem: [],
  };

  useEffect(() => {
    getMenus();
    if (!Object.keys(config).length) getConfig();
    if (!Object.keys(user).length) getUser();
    if (!Object.keys(params).length) getParam();

    setDefaultValues(def);
    setDefaultSelectedValues(def);
    getJokerFields();
    getItemCategoryLinks();
    getTagsLinks();
  }, []);

  useEffect(() => {
    const submenuId = getMenuSubMenuId('item');
    setSubmenu(submenuId);
    setCanEdit(getCanEdit(submenuId));
  }, [menus]);

  useEffect(() => {
    if (submenu) {
      initDefaultValues();
    }
  }, [submenu]);

  useEffect(() => {
    setSelectedValues(defaultSelectedValues);
  }, [defaultSelectedValues]);

  useEffect(() => {
    const syncFormValues = async () => {
      if (Object.keys(selectedValues).length > 0) {
        if (!isEqual(defaultSelectedValues, selectedValues)) {
          await valuesToForm();
          setOperatorsDisabled(false);
        } else {
          setOperatorsDisabled(true);
          setInitValues(defaultValues);
        }
      } else {
        setOperatorsDisabled(true);
        setInitValues(defaultValues);
      }
    };

    syncFormValues();
  }, [selectedValues]);

  useEffect(() => {
    if (resolvedItemCode) {
      itemService.getItemById(resolvedItemCode).then((data) => {
        setSelectedValues(data);
      });
    } else {
      setSelectedValues(defaultSelectedValues);
    }
  }, [defaultSelectedValues, resolvedItemCode]);

  const initializeItemCategoryTree = (nodes) => {
    return nodes
      .filter((node) => node.active)
      .map((node) => ({
        ...node,
        checked: false,
        children: initializeItemCategoryTree(node.children || []),
      }));
  };

  const applySelectedItemCategories = (nodes, selectedLinks = []) =>
    nodes.map((node) => ({
      ...node,
      checked: selectedLinks.some((link) => link.itemCategoryId === node.id),
      children: applySelectedItemCategories(node.children || [], selectedLinks),
    }));

  const collectSelectedItemCategoryIds = (nodes) =>
    nodes.flatMap((node) => [
      ...(node.checked ? [node.id] : []),
      ...collectSelectedItemCategoryIds(node.children || []),
    ]);

  const getItemCategoryLinks = () => {
    itemCategoryService.getData().then((data) => {
      const preparedData = initializeItemCategoryTree(data);

      setItemCategoryItemLink(preparedData);
      setInitValues((prev) => ({ ...prev, itemCategoryItemLink: preparedData }));
      setDefaultValues((prev) => ({ ...prev, itemCategoryItemLink: preparedData }));
    });
  };

  const getTagsLinks = () => {
    masterDataService.tagsByTargetToForm(TagTarget.ITEM).then((data) => {
      const preparedData = data.map((tag) => ({
        ...tag,
        checked: false,
      }));

      setTagItemLink(preparedData);
    });
  };

  const initDefaultValues = () => {
    const promiseArray = [];

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .quantityUnitToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .itemGroupToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .kshClassificationToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .vatCodeToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        formPrintTemplateService
          .getTemplates()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .companyToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    Promise.all(promiseArray)
      .then((resp) => {
        const aData = {
          quantityUnit: resp[0],
          itemGroup: resp[1],
          kshClassification: resp[2],
          vatCode: resp[3],
          company: resp[5],
        };
        setAutoCompleteData(aData);

        const suppliers = aData.company.filter((c) => c.active && c.supplier);

        const acOptions = {
          quantityUnit: generateAutocompleteItems(aData.quantityUnit, 'name', 'id'),
          itemGroup: generateAutocompleteItems(aData.itemGroup, 'name', 'id'),
          kshClassification: generateAutocompleteItems(aData.kshClassification, 'number', 'id'),
          purchaseVatCode: generateAutocompleteItems(
            aData.vatCode.filter((vc) => vc.category === CategoryOptionType.B),
            ['code', 'name'],
            'code'
          ),
          salesVatCode: generateAutocompleteItems(
            aData.vatCode.filter((vc) => vc.category === CategoryOptionType.K),
            ['code', 'name'],
            'code'
          ),
          supplier: generateAutocompleteItems(suppliers, 'name', 'id'),
        };
        setAutoCompleteOptions(acOptions);

        const formTemplates = resp[4];
        const itemTemplates = formTemplates.filter((template) => template.form?.url === 'item');

        const labelTemplate =
          itemTemplates.find(
            (template) => /\.zpl$/i.test(template?.name ?? '') && normalizeTemplateKey(template.key) === null
          ) ?? null;

        const batchLabelTemplate =
          itemTemplates.find(
            (template) =>
              /\.zpl$/i.test(template?.name ?? '') &&
              normalizeTemplateKey(template.key) === normalizeTemplateKey(ITEM_BATCH_LABEL_TEMPLATE_KEY)
          ) ?? null;

        const documentTemplates = itemTemplates
          .filter((template) => !/\.zpl$/i.test(template?.name ?? ''))
          .map((template) => ({
            ...template,
            displayName: getPrintableFormConfig(template.form?.url, template.key)?.name ?? template.name,
          }));

        setItemLabelTemplate(labelTemplate);
        setItemBatchLabelTemplate(batchLabelTemplate);
        setItemDocumentTemplates(documentTemplates);
      })
      .finally(() => {
        //hideLoader();
      });
  };

  const validationSchema = Yup.object().shape({
    itemCode: Yup.string().required(),
    autoGenerateItemCode: Yup.boolean().nullable(),
    itemName: Yup.string().required(),
    itemGroupId: Yup.string().required(),
    type: Yup.string().nullable(),
    kind: Yup.string().oneOf(Object.values(ItemKind)).nullable(),
    ownershipType: Yup.string().oneOf(Object.values(ItemOwnershipType)).nullable(),
    stockItemType: Yup.string().nullable(),
    inPurchase: Yup.boolean(),
    inSales: Yup.boolean(),
    weight: Yup.number()
      .nullable()
      .when('type', {
        is: (type) => type === ItemGroupType.STOCKARTICLE,
        then: (schema) =>
          schema.test(
            'is-decimal',
            `Max. ${config.decimalPlaces} tizedes.`,
            (value) =>
              value === undefined || new RegExp(`^\\d+(\\.\\d{1,${config.decimalPlaces}})?$`).test(String(value))
          ),
        otherwise: (schema) => schema,
      }),
    kshClassificationId: Yup.string().nullable(),
    other: Yup.string().nullable(),
    active: Yup.boolean(),
    invManagementMethod: Yup.string().oneOf(Object.values(InvManagementMethod)),
    quantityUnitId: Yup.string().required(),
    class1: Yup.boolean(),
    class2: Yup.boolean(),
    class3: Yup.boolean(),
    class4: Yup.boolean(),
    class5: Yup.boolean(),
    class6: Yup.boolean(),
    class7: Yup.boolean(),
    class8: Yup.boolean(),
    class9: Yup.boolean(),
    class10: Yup.boolean(),
    class11: Yup.boolean(),
    class12: Yup.boolean(),
    class13: Yup.boolean(),
    class14: Yup.boolean(),
    class15: Yup.boolean(),
    class16: Yup.boolean(),
    class17: Yup.boolean(),
    class18: Yup.boolean(),
    class19: Yup.boolean(),
    class20: Yup.boolean(),
    barCode: Yup.string().nullable(),
    landedCost: Yup.boolean(),
    purchaseVatCode: Yup.string().nullable(),
    drawingNumber: Yup.string().nullable(),
    salesVatCode: Yup.string().nullable(),
    defSupplier: Yup.string().nullable(),
    description: Yup.string().nullable(),
    itemTypeId: Yup.number().nullable(),
    itemPropertyAssignment: Yup.array()
      .nullable()
      .test('mandatory-item-properties', 'A kötelező tulajdonságok kitöltése szükséges.', (assignments = []) => {
        return assignments.every((assignment) => {
          if (!assignment?.property?.mandatory) {
            return true;
          }

          return hasItemPropertyAssignmentValue(assignment);
        });
      }),
    itemCategoryItemLink: Yup.array().of(
      Yup.object().shape({
        id: Yup.number().required(),
        name: Yup.string().required(),
        checked: Yup.boolean().required(),
      })
    ),
    tagItemLink: Yup.array().of(
      Yup.object().shape({
        id: Yup.number().required(),
        name: Yup.string().required(),
        checked: Yup.boolean().required(),
      })
    ),
    relatedItem: Yup.array().of(
      Yup.object().shape({
        relationType: Yup.string().required(),
        relatedItemCode: Yup.string().required(),
      })
    ),
    ...(jokerFields.length > 0
      ? Object.fromEntries(
          jokerFields.map((field) => {
            const fieldType = getJokerFieldType(field);
            return [
              field.field,
              fieldType === JokerFieldTypes.NUMBER ? Yup.number().nullable() : Yup.string().nullable(),
            ];
          })
        )
      : {}),
  });

  const resolveItemPropertyTypeId = async (selectedValue) => {
    if (selectedValue?.itemTypeId) {
      return selectedValue.itemTypeId;
    }

    const selectedAssignments = getSelectedItemPropertyAssignments(selectedValue);
    const firstAssignment = selectedAssignments[0];

    if (firstAssignment?.itemTypeProperty?.itemTypeId) {
      return firstAssignment.itemTypeProperty.itemTypeId;
    }

    if (Array.isArray(firstAssignment?.itemTypeProperty?.itemTypePropertyLink)) {
      return firstAssignment.itemTypeProperty.itemTypePropertyLink[0]?.itemTypeId ?? null;
    }

    const selectedPropertyIds = getSelectedItemPropertyIds(selectedValue);
    if (selectedPropertyIds.length === 0) {
      return null;
    }

    const data = await itemTypeService.getData();
    const itemTypeRows = Array.isArray(data) ? data : data?.rows ?? [];

    for (const itemTypeRow of itemTypeRows) {
      const itemTypeDetail = await itemTypeService.getItem(itemTypeRow.id);
      const itemTypePropertyIds = getItemTypePropertyIds(itemTypeDetail);
      const matchesAllAssignments = selectedPropertyIds.every((propertyId) => itemTypePropertyIds.includes(propertyId));

      if (matchesAllAssignments) {
        return itemTypeRow.id;
      }
    }

    return null;
  };

  const valuesToForm = async () => {
    const {
      itemCode,
      itemName,
      quantityUnitId,
      itemGroupId,
      type,
      kind,
      ownershipType,
      stockItemType,
      inPurchase,
      inSales,
      weight,
      kshClassificationId,
      other,
      active,
      invManagementMethod,
      itemPrice,
      class1,
      class2,
      class3,
      class4,
      class5,
      class6,
      class7,
      class8,
      class9,
      class10,
      class11,
      class12,
      class13,
      class14,
      class15,
      class16,
      class17,
      class18,
      class19,
      class20,
      joker_char1,
      joker_char2,
      joker_char3,
      joker_char4,
      joker_char5,
      joker_char6,
      joker_char7,
      joker_char8,
      joker_char9,
      joker_char10,
      joker_char11,
      joker_char12,
      joker_char13,
      joker_char14,
      joker_char15,
      joker_char16,
      joker_char17,
      joker_char18,
      joker_char19,
      joker_char20,
      joker_char21,
      joker_char22,
      joker_char23,
      joker_char24,
      joker_char25,
      joker_char26,
      joker_char27,
      joker_char28,
      joker_char29,
      joker_char30,
      joker_char31,
      joker_char32,
      joker_char33,
      joker_char34,
      joker_char35,
      joker_char36,
      joker_date1,
      joker_date2,
      joker_date3,
      joker_date4,
      joker_date5,
      joker_num1,
      joker_num2,
      joker_num3,
      joker_num4,
      joker_num5,
      joker_int1,
      joker_int2,
      joker_int3,
      joker_int4,
      joker_int5,
      barCode,
      landedCost,
      purchaseVatCode,
      salesVatCode,
      itemAttachment,
      drawingNumber,
      defSupplier,
      itemCategoryItemLink: itemCategoryItemLinkValues,
      description,
      tagItemLink: tagItemLinkValues,
      itemTypeId,
      itemPropertyAssignment,
      relatedItem,
    } = selectedValues;

    const updatedItemCategoryItemLink = applySelectedItemCategories(itemCategoryItemLink, itemCategoryItemLinkValues);

    const updatedTagItemLink = tagItemLink.filter((tagItem) =>
      tagItemLinkValues?.some((form) => form.tagId === tagItem.id)
    );

    const resolvedItemPropertyTypeId = await resolveItemPropertyTypeId(selectedValues);

    setInitValues({
      itemCode,
      itemName,
      quantityUnitId,
      itemGroupId,
      type,
      kind: kind ?? null,
      ownershipType: ownershipType ?? null,
      stockItemType,
      inPurchase,
      inSales,
      weight: weight ?? def.weight,
      kshClassificationId,
      other: other ?? '',
      active,
      invManagementMethod,
      class1,
      class2,
      class3,
      class4,
      class5,
      class6,
      class7,
      class8,
      class9,
      class10,
      class11,
      class12,
      class13,
      class14,
      class15,
      class16,
      class17,
      class18,
      class19,
      class20,
      itemPrice,
      joker_char1: joker_char1 ?? '',
      joker_char2: joker_char2 ?? '',
      joker_char3: joker_char3 ?? '',
      joker_char4: joker_char4 ?? '',
      joker_char5: joker_char5 ?? '',
      joker_char6: joker_char6 ?? '',
      joker_char7: joker_char7 ?? '',
      joker_char8: joker_char8 ?? '',
      joker_char9: joker_char9 ?? '',
      joker_char10: joker_char10 ?? '',
      joker_char11: joker_char11 ?? '',
      joker_char12: joker_char12 ?? '',
      joker_char13: joker_char13 ?? '',
      joker_char14: joker_char14 ?? '',
      joker_char15: joker_char15 ?? '',
      joker_char16: joker_char16 ?? '',
      joker_char17: joker_char17 ?? '',
      joker_char18: joker_char18 ?? '',
      joker_char19: joker_char19 ?? '',
      joker_char20: joker_char20 ?? '',
      joker_char21: joker_char21 ?? '',
      joker_char22: joker_char22 ?? '',
      joker_char23: joker_char23 ?? '',
      joker_char24: joker_char24 ?? '',
      joker_char25: joker_char25 ?? '',
      joker_char26: joker_char26 ?? '',
      joker_char27: joker_char27 ?? '',
      joker_char28: joker_char28 ?? '',
      joker_char29: joker_char29 ?? '',
      joker_char30: joker_char30 ?? '',
      joker_char31: joker_char31 ?? '',
      joker_char32: joker_char32 ?? '',
      joker_char33: joker_char33 ?? '',
      joker_char34: joker_char34 ?? '',
      joker_char35: joker_char35 ?? '',
      joker_char36: joker_char36 ?? '',
      joker_date1,
      joker_date2,
      joker_date3,
      joker_date4,
      joker_date5,
      joker_num1: joker_num1 ?? '',
      joker_num2: joker_num2 ?? '',
      joker_num3: joker_num3 ?? '',
      joker_num4: joker_num4 ?? '',
      joker_num5: joker_num5 ?? '',
      joker_int1: joker_int1 ?? '',
      joker_int2: joker_int2 ?? '',
      joker_int3: joker_int3 ?? '',
      joker_int4: joker_int4 ?? '',
      joker_int5: joker_int5 ?? '',
      barCode: barCode ?? '',
      landedCost,
      purchaseVatCode,
      salesVatCode,
      itemAttachment,
      drawingNumber: drawingNumber ?? '',
      defSupplier,
      itemTypeId: itemTypeId ?? resolvedItemPropertyTypeId ?? null,
      itemPropertyAssignment: itemPropertyAssignment ?? [],
      itemCategoryItemLink: updatedItemCategoryItemLink,
      description: description ?? '',
      tagItemLink: updatedTagItemLink,
      relatedItem: relatedItem?.map((rItem) => ({
        id: rItem.id,
        relationType: rItem.relationType,
        relatedItemCode: rItem.relatedItemCode,
      })),
    });
  };

  const buildItemPropertyAssignmentPayload = (assignments = []) => {
    return assignments
      .filter((assignment) => {
        const dataType = assignment.property?.dataType;
        const hasExistingRow = Boolean(assignment.id);

        if (dataType === 'COMBO') {
          return hasExistingRow || Boolean(assignment.itemTypePropertyValueId);
        }

        if (dataType === 'NUMBER' || dataType === 'DECIMAL') {
          return (
            hasExistingRow ||
            (assignment.valueNumber !== '' && assignment.valueNumber !== null && assignment.valueNumber !== undefined)
          );
        }

        return hasExistingRow || Boolean(assignment.valueText?.trim());
      })
      .map((assignment) => {
        const dataType = assignment.property?.dataType;
        const usesTextValue = dataType !== 'COMBO' && dataType !== 'NUMBER' && dataType !== 'DECIMAL';
        const basePayload = {
          ...(assignment.id ? { id: assignment.id, updatedBy: user.userId } : { createdBy: user.userId }),
          itemTypePropertyId: assignment.itemTypePropertyId,
          itemTypePropertyValueId: dataType === 'COMBO' ? assignment.itemTypePropertyValueId || null : null,
          valueText: usesTextValue ? assignment.valueText?.trim() || null : null,
          valueNumber:
            dataType === 'NUMBER' || dataType === 'DECIMAL'
              ? assignment.valueNumber === ''
                ? null
                : parseFloat(assignment.valueNumber)
              : null,
          valueCombo: null,
        };

        return basePayload;
      });
  };

  const refreshSelectedItem = async (nextItemCode) => {
    if (!nextItemCode) return null;

    const detail = await itemService.getItemById(nextItemCode);
    setSelectedValues(detail);

    return detail;
  };

  const handleSubmit = (values, { resetForm }) => {
    if (!editButtonVisible) {
      updateObject(values);
    } else if (!addField) {
      handleInsertObject(values, resetForm);
    }
  };

  const updateObject = async (values) => {
    const { itemPropertyAssignment, ...restValues } = values;
    const updData = {
      ...cleanValues(restValues),
      updatedBy: user.userId,
      weight: parseFloat(values.weight),
      itemCategoryItemLink: collectSelectedItemCategoryIds(values.itemCategoryItemLink),
      tagItemLink: values.tagItemLink.map((link) => link.id),
      itemPropertyAssignment: buildItemPropertyAssignmentPayload(itemPropertyAssignment),
    };

    updData.existingFiles = updData.itemAttachment?.map((f) => ({
      id: f.id,
      active: f.active,
    }));

    const formData = new FormData();
    formData.append(
      'data',
      JSON.stringify({
        ...updData,
        newFiles: updData.itemAttachment
          .filter((x) => x instanceof File)
          .map((x) => ({
            name: x.name,
            active: x.active,
          })),
      })
    );

    if (updData.itemAttachment) {
      for (const file of updData.itemAttachment) {
        if (file instanceof File) {
          formData.append('files', file);
        }
      }
    }

    itemService.updateItem(formData, selectedValues.itemCode).then(async (data) => {
      addNotification({
        content: 'Sikeres módosítás!',
        type: NotificationType.SUCCESS,
      });

      await refreshSelectedItem(data?.itemCode ?? selectedValues.itemCode);
      getJokerFields();
      setJokerRefreshKey((prev) => prev + 1);
      setRefetchTable(true);
      setEditButtonvisible(true);
      setViewField(true);
    });
  };

  const handleInsertObject = async (values) => {
    const { autoGenerateItemCode, itemPropertyAssignment, ...restValues } = values;
    const insertObject = {
      ...cleanValues(restValues),
      createdBy: user.userId,
      weight: parseFloat(values.weight),
      ...(isCustomItemCodeGenerationEnabled(config) ? { autoGenerate: autoGenerateItemCode ?? false } : {}),
      itemCategoryItemLink: collectSelectedItemCategoryIds(values.itemCategoryItemLink),
      tagItemLink: values.tagItemLink.map((link) => link.id),
      itemPropertyAssignment: buildItemPropertyAssignmentPayload(itemPropertyAssignment),
    };

    const formData = new FormData();

    formData.append(
      'data',
      JSON.stringify({
        ...insertObject,
        newFiles: insertObject.itemAttachment
          .filter((x) => x instanceof File)
          .map((x) => ({
            name: x.name,
            active: x.active,
          })),
      })
    );

    if (insertObject.itemAttachment) {
      for (const file of insertObject.itemAttachment) {
        formData.append('files', file);
      }
    }

    itemService
      .createItem(formData)
      .then(async (data) => {
        addNotification({
          content: 'Sikeres mentés!',
          type: NotificationType.SUCCESS,
        });

        const refreshedItem = await refreshSelectedItem(data?.itemCode);
        getJokerFields();
        setJokerRefreshKey((prev) => prev + 1);
        setRowSelectionModel(refreshedItem?.itemCode ?? data.itemCode);
        setRefetchTable(true);
        setAddField(true);
        setViewField(true);
      })
      .finally(() => {
        //@TODO loader
      });
  };

  dayjs.extend(utc);

  const handleRemoveElement = async () => {
    const deleteData = {
      deletedBy: user.userId,
    };
    itemService.deleteItem(deleteData, selectedValues.itemCode).then(() => {
      addNotification({
        content: 'Sikeres törlés!',
        type: NotificationType.WARNING,
      });
      setRefetchTable(true);
      setShowDeleteDialog(false);
      setSelectedValues(defaultSelectedValues);
    });
  };

  const handleTemplate = () => {
    itemService.getExcelTemplate().then(() => {
      addNotification({
        content: 'A letöltés elindult!',
        type: NotificationType.SUCCESS,
      });
    });
  };

  const handleExport = () => {
    itemService.getExcelData().then(() => {
      addNotification({
        content: 'A letöltés elindult!',
        type: NotificationType.SUCCESS,
      });
    });
  };

  const handleImport = (files) => {
    itemService.importExcel(files, user.userId).then(() => {
      setRefetchTable(true);
      addNotification({
        content: 'Az import sikeres!',
        type: NotificationType.SUCCESS,
      });
    });
  };

  const onStockInfoClick = () => {
    setItemStockDialogState({ open: true, data: selectedValues });
    setContextMenu(defContextMenu);
  };

  const hasLabelTemplate = Boolean(itemLabelTemplate?.id);
  const hasBatchLabelTemplate = Boolean(itemBatchLabelTemplate?.id);
  const hasDocumentTemplate = itemDocumentTemplates.length > 0;

  const hasAnyLabelTemplate = hasLabelTemplate || hasBatchLabelTemplate;
  const isUserLoaded = Object.keys(user ?? {}).length > 0;
  const itemGroupOptionsLoaded = Array.isArray(autoCompleteData?.itemGroup);
  const currentUserRoleId = user?.roleId ?? null;
  const hiddenItemGroupIds = Array.isArray(autoCompleteData?.itemGroup)
    ? autoCompleteData.itemGroup
        .filter(
          (itemGroup) =>
            currentUserRoleId !== null &&
            Array.isArray(itemGroup?.roleExcludedItemGroup) &&
            itemGroup.roleExcludedItemGroup.some((excludedRole) => excludedRole?.roleId === currentUserRoleId)
        )
        .map((itemGroup) => itemGroup.id)
    : [];
  const filteredAutocompleteOptions = {
    ...autocompleteOptions,
    itemGroup: autocompleteOptions.itemGroup.filter((itemGroup) => !hiddenItemGroupIds.includes(itemGroup.value)),
  };

  const mainContent = (
    <>
      <div
        className="h-full overflow-auto custom-scrollbar"
        onContextMenu={(e) => {
          if (!viewField || operatorsDisabled) return;

          e.preventDefault();
          setContextMenu({
            position: { mouseX: e.clientX + 2, mouseY: e.clientY - 6 },
          });
        }}
      >
        <div className="flex items-center flex-wrap justify-between">
          <div className="flex items-center flex-wrap">
            <FormHeaderUI
              addClick={() => {
                setSelectedValues(defaultSelectedValues);
                setViewField(false);
                setAddField(false);
              }}
              addDisabled={!viewField || !canEdit}
              modClick={() => {
                setViewField(false);
                setEditButtonvisible(false);
              }}
              modDisabled={!viewField || operatorsDisabled || !canEdit}
              deleteClick={() => {
                setShowDeleteDialog(true);
              }}
              deleteDisabled={!viewField || operatorsDisabled || !canEdit}
              copyClick={() => {
                setViewField(false);
                setAddField(false);
              }}
              copyDisabled={!viewField || operatorsDisabled || !canEdit}
            />
            {hasDocumentTemplate && (
              <ButtonUI
                text="Nyomtatás"
                size="sm"
                className="bg-labelColor py-3"
                onClick={() => setAttachmentPrintDialogOpen(true)}
                disabled={!viewField || operatorsDisabled}
              />
            )}
            {hasAnyLabelTemplate && (
              <ButtonUI
                text="Címke"
                size="sm"
                className="bg-labelColor py-3"
                onClick={() =>
                  setLabelDialogState({
                    open: true,
                    type: selectedValues.invManagementMethod === InvManagementMethod.BATCH ? 'batch' : 'simple',
                  })
                }
                disabled={!viewField || operatorsDisabled}
              />
            )}
            {params?.ENABLE_SUPPORTING_DOC === 'Y' && (
              <Tooltip title="Kísérő dokumentumok">
                <span>
                  <IconButton
                    onClick={() => setSupportingDocumentTypeDialogOpen(true)}
                    disabled={!viewField || operatorsDisabled || !selectedValues?.itemCode}
                  >
                    <FilePresentOutlinedIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </div>
          <History disabled={operatorsDisabled} onClick={() => setHistoryDialogOpen(!historyDialogOpen)} />
        </div>

        <Formik
          initialValues={initValues}
          validationSchema={validationSchema}
          validateOnChange={false}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ resetForm }) => (
            <Form>
              <ItemFields
                viewField={viewField}
                selectedValues={selectedValues}
                autocompleteOptions={filteredAutocompleteOptions}
                addField={addField}
                autoCompleteData={autoCompleteData}
                config={config}
              />
              {!isLoading && (
                <ItemTabs
                  selectedValues={selectedValues}
                  viewField={viewField}
                  setDefaultValues={setDefaultValues}
                  handleImport={handleImport}
                  handleExport={handleExport}
                  handleTemplate={handleTemplate}
                  setRefetchTable={setRefetchTable}
                  setSelectedValues={setSelectedValues}
                  addField={addField}
                  jokerFields={jokerFields}
                  jokerRefreshKey={jokerRefreshKey}
                  disabled={viewField}
                  autocompleteOptions={autocompleteOptions}
                  autoCompleteData={autoCompleteData}
                />
              )}
              <FormButtonPanelUI
                onCancelClick={() => {
                  setViewField(true);
                  setEditButtonvisible(true);
                  setAddField(true);

                  resetForm();
                }}
                disabled={editButtonVisible && addField}
              />
            </Form>
          )}
        </Formik>
      </div>
    </>
  );

  return (
    <>
      {hideRightPanel ? (
        mainContent
      ) : (
        <FormBase
          name="Item"
          leftPanelContent={
            <>
              <div
                className="h-full overflow-auto custom-scrollbar"
                onContextMenu={(e) => {
                  if (!viewField || operatorsDisabled) return;

                  e.preventDefault();
                  setContextMenu({
                    position: { mouseX: e.clientX + 2, mouseY: e.clientY - 6 },
                  });
                }}
              >
                <div className="flex items-center flex-wrap justify-between">
                  <div className="flex items-center flex-wrap">
                    <FormHeaderUI
                      addClick={() => {
                        setSelectedValues(defaultSelectedValues);
                        setViewField(false);
                        setAddField(false);
                      }}
                      addDisabled={!viewField || !canEdit}
                      modClick={() => {
                        setViewField(false);
                        setEditButtonvisible(false);
                      }}
                      modDisabled={!viewField || operatorsDisabled || !canEdit}
                      deleteClick={() => {
                        setShowDeleteDialog(true);
                      }}
                      deleteDisabled={!viewField || operatorsDisabled || !canEdit}
                      copyClick={() => {
                        setViewField(false);
                        setAddField(false);
                      }}
                      copyDisabled={!viewField || operatorsDisabled || !canEdit}
                    />
                    {hasDocumentTemplate && (
                      <ButtonUI
                        text="Nyomtatás"
                        size="sm"
                        className="bg-labelColor py-3"
                        onClick={() => setAttachmentPrintDialogOpen(true)}
                        disabled={!viewField || operatorsDisabled}
                      />
                    )}
                    {hasAnyLabelTemplate && (
                      <ButtonUI
                        text="Címke"
                        size="sm"
                        className="bg-labelColor py-3"
                        onClick={() =>
                          setLabelDialogState({
                            open: true,
                            type: selectedValues.invManagementMethod === InvManagementMethod.BATCH ? 'batch' : 'simple',
                          })
                        }
                        disabled={!viewField || operatorsDisabled}
                      />
                    )}
                    {params?.ENABLE_SUPPORTING_DOC === 'Y' && (
                      <Tooltip title="Kísérő dokumentumok">
                        <span>
                          <IconButton
                            onClick={() => setSupportingDocumentTypeDialogOpen(true)}
                            disabled={!viewField || operatorsDisabled || !selectedValues?.itemCode}
                          >
                            <FilePresentOutlinedIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </div>
                  <History disabled={operatorsDisabled} onClick={() => setHistoryDialogOpen(!historyDialogOpen)} />
                </div>

                <Formik
                  initialValues={initValues}
                  validationSchema={validationSchema}
                  validateOnChange={false}
                  onSubmit={handleSubmit}
                  enableReinitialize
                >
                  {({ resetForm }) => (
                    <Form>
                      <ItemFields
                        viewField={viewField}
                        selectedValues={selectedValues}
                        autocompleteOptions={filteredAutocompleteOptions}
                        addField={addField}
                        autoCompleteData={autoCompleteData}
                        config={config}
                      />
                      {!isLoading && (
                        <ItemTabs
                          selectedValues={selectedValues}
                          viewField={viewField}
                          setDefaultValues={setDefaultValues}
                          handleImport={handleImport}
                          handleExport={handleExport}
                          handleTemplate={handleTemplate}
                          setRefetchTable={setRefetchTable}
                          setSelectedValues={setSelectedValues}
                          addField={addField}
                          jokerFields={jokerFields}
                          jokerRefreshKey={jokerRefreshKey}
                          disabled={viewField}
                          autocompleteOptions={autocompleteOptions}
                          autoCompleteData={autoCompleteData}
                          tagItemLink={tagItemLink}
                          defRelatedItem={defRelatedItem}
                        />
                      )}
                      <FormButtonPanelUI
                        onCancelClick={() => {
                          setViewField(true);
                          setEditButtonvisible(true);
                          setAddField(true);

                          resetForm();
                        }}
                        disabled={editButtonVisible && addField}
                      />
                    </Form>
                  )}
                </Formik>
              </div>
            </>
          }
          rightPanelContent={
            hideRightPanel ? (
              <></>
            ) : (
              isUserLoaded &&
              itemGroupOptionsLoaded && (
                <ItemReports
                  refetch={{ refetchTable, setRefetchTable }}
                  values={{ selectedValues, setSelectedValues }}
                  viewField={viewField}
                  operatorsDisabled={operatorsDisabled}
                  rowSelection={{ rowSelectionModel, setRowSelectionModel }}
                  autocompleteOptions={filteredAutocompleteOptions}
                  hiddenItemGroupIds={hiddenItemGroupIds}
                  jokerFields={jokerFields}
                />
              )
            )
          }
        />
      )}
      <DeleteDialogUI
        handleRemoveElement={handleRemoveElement}
        show={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
      />

      <ItemLabelDialog
        open={hasLabelTemplate && labelDialogState.open && labelDialogState.type !== 'batch'}
        onClose={() => setLabelDialogState({ open: false })}
        selectedValuesState={{ selectedValues, setSelectedValues }}
        templateKey={null}
      />

      <ItemBatchLabelDialog
        open={hasBatchLabelTemplate && labelDialogState.open && labelDialogState.type === 'batch'}
        onClose={() => setLabelDialogState({ open: false })}
        selectedValuesState={{ selectedValues, setSelectedValues }}
        templateKey={ITEM_BATCH_LABEL_TEMPLATE_KEY}
      />

      <ItemAttachmentPrintDialog
        open={attachmentPrintDialogOpen}
        onClose={() => setAttachmentPrintDialogOpen(false)}
        itemCode={selectedValues?.itemCode}
        printTemplates={itemDocumentTemplates}
      />

      <ItemStockDialog
        open={itemStockDialogState.open}
        data={itemStockDialogState.data}
        onClose={handleItemStockDialogClose}
      />

      <ContextMenu
        contextMenu={contextMenu}
        handleClose={() => setContextMenu(defContextMenu)}
        onStockInfoClick={onStockInfoClick}
      />

      <ItemHistoryDialog
        open={historyDialogOpen}
        onClose={() => setHistoryDialogOpen(false)}
        itemCode={selectedValues.itemCode}
      />

      <SupportingDocumentTypeItemLinkDialog
        open={supportingDocumentTypeDialogOpen}
        onClose={() => setSupportingDocumentTypeDialogOpen(false)}
        itemCode={selectedValues?.itemCode}
        editable
        batchManaged={selectedValues?.invManagementMethod === InvManagementMethod.BATCH}
      />
    </>
  );
};
